import { useCallback, useEffect, useRef, useState } from 'react';
import { TranscribeWorkerDevice, TranscribeWorkerOutMessage } from '../workers/transcribe.types';
import {
  decodeToMono16k,
  downloadAudio,
  DownloadMode,
  DownloadProgressCallback,
  DownloadProgressInfo,
} from '../utils/transcript/audio';
import { chunksToLrc, TranscriptChunk } from '../utils/transcript/lrc';

export type TranscriberStage =
  | 'idle'
  | 'loading-model'
  | 'downloading-audio'
  | 'decoding'
  | 'transcribing'
  | 'done'
  | 'error';

export interface TranscribeTimingsMs {
  modelLoad: number;
  audioDownload: number;
  decode: number;
  transcribe: number;
  total: number;
}

export interface TranscribeResult {
  lrc: string;
  chunks: TranscriptChunk[];
  device: TranscribeWorkerDevice;
  timingsMs: TranscribeTimingsMs;
  downloadMode: DownloadMode | 'prefetched';
  audioBytes: number;
}

export interface DownloadProgressState {
  percent: number;
  loadedBytes: number;
  totalBytes: number;
  etaSeconds: number | null;
}

export interface TranscribeProgressState {
  percent: number;
  etaSeconds: number | null;
  /** `true` when `percent` is derived from an elapsed-time estimate rather than a real progress signal. */
  estimated: boolean;
}

interface PendingJob {
  resolve: (result: TranscribeResult) => void;
  reject: (error: Error) => void;
}

interface PreparedAudio {
  audio: Float32Array;
  audioDownloadMs: number;
  decodeMs: number;
  downloadMode: DownloadMode;
  audioBytes: number;
}

interface PrefetchEntry {
  promise: Promise<PreparedAudio>;
  controller: AbortController;
}

const MAX_PREFETCH_ENTRIES = 2;

// If the worker goes this long without reporting transcription progress for the
// active job, treat it as stalled (e.g. a hallucination/repetition loop that never
// hits a chunk boundary) rather than hanging the batch loop forever.
const TRANSCRIBE_STALL_TIMEOUT_MS = 180_000;

// Default seconds-of-wallclock-time-per-second-of-audio ratios used to estimate
// transcription progress when the worker hasn't reported a real chunk boundary
// yet. Refined at runtime from the last completed job's measured throughput.
const DEFAULT_RATIO_WEBGPU = 0.08;
const DEFAULT_RATIO_WASM = 0.5;
const ESTIMATE_CAP_PERCENT = 95;
const RATIO_STORAGE_KEY = 'transcribe.transcribeRatio';

interface StoredRatios {
  webgpu?: number;
  wasm?: number;
}

const readStoredRatios = (): StoredRatios => {
  try {
    const raw = window.localStorage.getItem(RATIO_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredRatios) : {};
  } catch {
    return {};
  }
};

const writeStoredRatio = (device: TranscribeWorkerDevice, ratio: number) => {
  try {
    const current = readStoredRatios();
    current[device] = ratio;
    window.localStorage.setItem(RATIO_STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Ignore storage failures (private browsing, quota, etc.) - estimation just
    // falls back to the in-memory ratio for the rest of the session.
  }
};

const createWorker = () => new Worker(new URL('../workers/transcribe.worker.ts', import.meta.url), { type: 'module' });

const prepareAudio = async (
  audioPath: string,
  signal: AbortSignal,
  onProgress?: DownloadProgressCallback,
): Promise<PreparedAudio> => {
  const downloadStart = performance.now();
  const { arrayBuffer, bytes, mode } = await downloadAudio(audioPath, onProgress, signal);
  const audioDownloadMs = performance.now() - downloadStart;

  const decodeStart = performance.now();
  const audio = await decodeToMono16k(arrayBuffer);
  const decodeMs = performance.now() - decodeStart;

  return { audio, audioDownloadMs, decodeMs, downloadMode: mode, audioBytes: bytes };
};

export interface UseTranscriberOptions {
  /** Starts loading the ASR model as soon as the hook mounts. */
  preload?: boolean;
}

/**
 * Wraps the transcription Web Worker. Only one lesson is transcribed at a
 * time: concurrent `transcribe()` calls are queued and processed in order,
 * which is what the future batch-processing flow will rely on.
 */
export const useTranscriber = (options: UseTranscriberOptions = {}) => {
  const { preload = false } = options;

  const workerRef = useRef<Worker | null>(null);
  const readyRef = useRef<Promise<TranscribeWorkerDevice> | null>(null);
  const deviceRef = useRef<TranscribeWorkerDevice | null>(null);
  const pendingJobsRef = useRef<Map<string, PendingJob>>(new Map());
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const prefetchCacheRef = useRef<Map<string, PrefetchEntry>>(new Map());
  const activeControllerRef = useRef<AbortController | null>(null);
  const activeJobIdRef = useRef<string | null>(null);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refined per-device ratios (measured seconds of wallclock per second of audio),
  // seeded from localStorage and updated after every completed job.
  const transcribeRatiosRef = useRef<StoredRatios>(readStoredRatios());
  const estimateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTranscribePercentRef = useRef(0);
  const gotRealTranscribeProgressRef = useRef(false);
  const activeJobStartedAtRef = useRef(0);

  const [stage, setStage] = useState<TranscriberStage>('idle');
  const [progress, setProgress] = useState<number | null>(null);
  const [device, setDevice] = useState<TranscribeWorkerDevice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modelLoadProgress, setModelLoadProgress] = useState<number | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgressState | null>(null);
  const [transcribeProgress, setTranscribeProgress] = useState<TranscribeProgressState | null>(null);

  const clearEstimateTimer = () => {
    if (estimateTimerRef.current !== null) {
      clearInterval(estimateTimerRef.current);
      estimateTimerRef.current = null;
    }
  };

  const clearStallTimer = () => {
    if (stallTimerRef.current !== null) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  };

  // Rearms the stall watchdog for the active transcribe job. Called when the
  // `transcribe` message is sent and again on every `transcribe-progress` for that
  // job, so only a genuine stall (no progress at all for the timeout window) fires it.
  const armStallTimer = useCallback((id: string) => {
    clearStallTimer();
    stallTimerRef.current = setTimeout(() => {
      if (activeJobIdRef.current !== id) {
        return;
      }
      const job = pendingJobsRef.current.get(id);
      pendingJobsRef.current.delete(id);
      workerRef.current?.terminate();
      workerRef.current = null;
      readyRef.current = null;
      deviceRef.current = null;
      setDevice(null);
      activeControllerRef.current = null;
      activeJobIdRef.current = null;
      clearEstimateTimer();
      setStage('error');
      setError('Transcription stalled (no progress for 3 minutes). Try again.');
      job?.reject(new Error('Transcription stalled (no progress for 3 minutes). Try again.'));
    }, TRANSCRIBE_STALL_TIMEOUT_MS);
  }, []);

  const resetJobProgress = useCallback(() => {
    clearEstimateTimer();
    setDownloadProgress(null);
    setTranscribeProgress(null);
    setProgress(null);
    lastTranscribePercentRef.current = 0;
    gotRealTranscribeProgressRef.current = false;
  }, []);

  const applyTranscribePercent = useCallback((percent: number, elapsedMs: number, estimated: boolean) => {
    const clamped = Math.max(lastTranscribePercentRef.current, Math.min(100, percent));
    lastTranscribePercentRef.current = clamped;
    const etaSeconds = clamped > 3 ? (elapsedMs / 1000) * ((100 - clamped) / clamped) : null;
    setTranscribeProgress({ percent: clamped, etaSeconds, estimated });
    setProgress(clamped);
  }, []);

  const startEstimateTimer = useCallback(
    (totalSeconds: number, deviceForEstimate: TranscribeWorkerDevice) => {
      clearEstimateTimer();
      const defaultRatio = deviceForEstimate === 'webgpu' ? DEFAULT_RATIO_WEBGPU : DEFAULT_RATIO_WASM;
      const ratio = transcribeRatiosRef.current[deviceForEstimate] ?? defaultRatio;
      const expectedMs = Math.max(totalSeconds, 0) * ratio * 1000;
      const startedAt = performance.now();

      const tick = () => {
        if (gotRealTranscribeProgressRef.current) {
          clearEstimateTimer();
          return;
        }
        const elapsedMs = performance.now() - startedAt;
        const rawPercent = expectedMs > 0 ? (elapsedMs / expectedMs) * 100 : 0;
        const cappedPercent = Math.min(ESTIMATE_CAP_PERCENT, rawPercent);
        applyTranscribePercent(cappedPercent, elapsedMs, true);
      };

      tick();
      estimateTimerRef.current = setInterval(tick, 500);
    },
    [applyTranscribePercent],
  );

  const attachWorker = useCallback(() => {
    const worker = createWorker();

    worker.addEventListener('message', (event: MessageEvent<TranscribeWorkerOutMessage>) => {
      const message = event.data;

      if (message.type === 'download-progress') {
        setModelLoadProgress(message.progress);
        setProgress((current) => (current === null ? message.progress : current));
        return;
      }

      if (message.type === 'ready') {
        deviceRef.current = message.device;
        setDevice(message.device);
        setModelLoadProgress(null);
        setStage((current) => (current === 'loading-model' ? 'idle' : current));
        return;
      }

      if (message.type === 'transcribe-progress') {
        if (message.id !== activeJobIdRef.current) {
          return;
        }
        gotRealTranscribeProgressRef.current = true;
        clearEstimateTimer();
        armStallTimer(message.id);
        const elapsedMs = performance.now() - activeJobStartedAtRef.current;
        applyTranscribePercent(message.percent, elapsedMs, false);
        return;
      }

      if (message.type === 'result') {
        clearStallTimer();
        const job = pendingJobsRef.current.get(message.id);
        pendingJobsRef.current.delete(message.id);
        job?.resolve({
          lrc: chunksToLrc(message.chunks),
          chunks: message.chunks,
          device: deviceRef.current ?? 'wasm',
          // Filled in by the caller (transcribe()) which owns the timing spans.
          timingsMs: { modelLoad: 0, audioDownload: 0, decode: 0, transcribe: 0, total: 0 },
          downloadMode: 'single',
          audioBytes: 0,
        });
        return;
      }

      if (message.type === 'error') {
        clearStallTimer();
        setError(message.message);
        setStage('error');
        if (message.id) {
          const job = pendingJobsRef.current.get(message.id);
          pendingJobsRef.current.delete(message.id);
          job?.reject(new Error(message.message));
        }
      }
    });

    workerRef.current = worker;
    return worker;
  }, [applyTranscribePercent, armStallTimer]);

  const ensureWorker = useCallback(() => {
    if (!workerRef.current) {
      attachWorker();
    }
    return workerRef.current as Worker;
  }, [attachWorker]);

  const ensureReady = useCallback(() => {
    const worker = ensureWorker();
    if (!readyRef.current) {
      setStage((current) => (current === 'idle' ? 'loading-model' : current));
      readyRef.current = new Promise<TranscribeWorkerDevice>((resolve, reject) => {
        const onMessage = (event: MessageEvent<TranscribeWorkerOutMessage>) => {
          const message = event.data;
          if (message.type === 'ready') {
            worker.removeEventListener('message', onMessage);
            resolve(message.device);
          } else if (message.type === 'error' && !message.id) {
            worker.removeEventListener('message', onMessage);
            reject(new Error(message.message));
          }
        };
        worker.addEventListener('message', onMessage);
        worker.postMessage({ type: 'load' });
      });
    }
    return readyRef.current;
  }, [ensureWorker]);

  const [prefetchProgress, setPrefetchProgress] = useState<Record<string, number>>({});

  const startPrefetch = useCallback((audioPath: string, onProgress?: (percent: number) => void) => {
    const cache = prefetchCacheRef.current;
    if (cache.has(audioPath)) {
      return;
    }

    // Keep the cache small: drop the oldest entry once we're at capacity.
    if (cache.size >= MAX_PREFETCH_ENTRIES) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) {
        cache.get(oldestKey)?.controller.abort();
        cache.delete(oldestKey);
        setPrefetchProgress((current) => {
          const next = { ...current };
          delete next[oldestKey];
          return next;
        });
      }
    }

    const controller = new AbortController();
    setPrefetchProgress((current) => ({ ...current, [audioPath]: 0 }));

    const promise = prepareAudio(audioPath, controller.signal, (info: DownloadProgressInfo) => {
      setPrefetchProgress((current) => ({ ...current, [audioPath]: info.percent }));
      onProgress?.(info.percent);
    }).catch((prefetchError) => {
      // Don't cache a failed prefetch; let a subsequent transcribe() retry it normally.
      cache.delete(audioPath);
      setPrefetchProgress((current) => {
        const next = { ...current };
        delete next[audioPath];
        return next;
      });
      throw prefetchError;
    });
    cache.set(audioPath, { promise, controller });
  }, []);

  const prefetch = useCallback(
    (audioPath: string, onProgress?: (percent: number) => void) => {
      startPrefetch(audioPath, onProgress);
    },
    [startPrefetch],
  );

  const runTranscribe = useCallback(
    async (audioPath: string): Promise<TranscribeResult> => {
      const totalStart = performance.now();
      setError(null);
      resetJobProgress();

      const controller = new AbortController();
      activeControllerRef.current = controller;

      const cache = prefetchCacheRef.current;
      const cached = cache.get(audioPath);
      if (cached) {
        cache.delete(audioPath);
        setPrefetchProgress((current) => {
          const next = { ...current };
          delete next[audioPath];
          return next;
        });
      }

      try {
        let prepared: PreparedAudio;
        let downloadMode: DownloadMode | 'prefetched';
        let modelLoadMs: number;

        if (cached) {
          setStage('downloading-audio');
          const modelLoadStart = performance.now();
          const [, preparedAudio] = await Promise.all([ensureReady(), cached.promise]);
          modelLoadMs = performance.now() - modelLoadStart;
          prepared = preparedAudio;
          downloadMode = 'prefetched';
        } else {
          setStage('downloading-audio');
          const modelLoadStart = performance.now();
          const downloadStartedAt = performance.now();
          const [, preparedAudio] = await Promise.all([
            ensureReady(),
            prepareAudio(audioPath, controller.signal, (info: DownloadProgressInfo) => {
              setProgress(info.percent);
              const elapsedSeconds = (performance.now() - downloadStartedAt) / 1000;
              const bytesPerSecond = elapsedSeconds > 0 ? info.loadedBytes / elapsedSeconds : 0;
              const remainingBytes = Math.max(0, info.totalBytes - info.loadedBytes);
              const etaSeconds = bytesPerSecond > 0 ? remainingBytes / bytesPerSecond : null;
              setDownloadProgress({
                percent: info.percent,
                loadedBytes: info.loadedBytes,
                totalBytes: info.totalBytes,
                etaSeconds,
              });
            }),
          ]);
          modelLoadMs = performance.now() - modelLoadStart;
          prepared = preparedAudio;
          downloadMode = prepared.downloadMode;
        }

        setStage('decoding');
        setProgress(null);
        setDownloadProgress(null);

        setStage('transcribing');
        const transcribeStart = performance.now();
        const worker = ensureWorker();
        const id = crypto.randomUUID();
        activeJobIdRef.current = id;
        activeJobStartedAtRef.current = transcribeStart;

        const totalAudioSeconds = prepared.audio.length / 16000;
        startEstimateTimer(totalAudioSeconds, deviceRef.current ?? 'wasm');

        const result = await new Promise<TranscribeResult>((resolve, reject) => {
          pendingJobsRef.current.set(id, { resolve, reject });
          armStallTimer(id);
          worker.postMessage({ type: 'transcribe', id, audio: prepared.audio }, [prepared.audio.buffer]);
        });
        const transcribeMs = performance.now() - transcribeStart;
        clearEstimateTimer();
        clearStallTimer();

        // Refine the ratio used for future estimates from this job's measured throughput.
        const finishedDevice = deviceRef.current ?? 'wasm';
        if (totalAudioSeconds > 0) {
          const measuredRatio = transcribeMs / 1000 / totalAudioSeconds;
          transcribeRatiosRef.current[finishedDevice] = measuredRatio;
          writeStoredRatio(finishedDevice, measuredRatio);
        }

        const total = performance.now() - totalStart;
        const finalResult: TranscribeResult = {
          ...result,
          timingsMs: {
            modelLoad: modelLoadMs,
            audioDownload: prepared.audioDownloadMs,
            decode: prepared.decodeMs,
            transcribe: transcribeMs,
            total,
          },
          downloadMode,
          audioBytes: prepared.audioBytes,
        };

        setStage('done');
        resetJobProgress();
        return finalResult;
      } finally {
        activeControllerRef.current = null;
        activeJobIdRef.current = null;
        clearEstimateTimer();
        clearStallTimer();
      }
    },
    [armStallTimer, ensureReady, ensureWorker, resetJobProgress, startEstimateTimer],
  );

  const transcribe = useCallback(
    (audioPath: string): Promise<TranscribeResult> => {
      const job = queueRef.current.then(() => runTranscribe(audioPath));
      // Swallow errors on the queue chain itself so one failed job doesn't
      // permanently break the queue for subsequent calls; the caller's
      // promise (`job`) still rejects normally.
      queueRef.current = job.then(
        () => undefined,
        () => undefined,
      );
      return job;
    },
    [runTranscribe],
  );

  const cancel = useCallback(() => {
    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
    activeJobIdRef.current = null;
    clearStallTimer();

    prefetchCacheRef.current.forEach((entry) => entry.controller.abort());
    prefetchCacheRef.current.clear();
    setPrefetchProgress({});

    workerRef.current?.terminate();
    workerRef.current = null;
    readyRef.current = null;
    deviceRef.current = null;
    setDevice(null);
    setStage('idle');
    resetJobProgress();
    setModelLoadProgress(null);

    pendingJobsRef.current.forEach((job) => job.reject(new Error('Transcription cancelled')));
    pendingJobsRef.current.clear();
    queueRef.current = Promise.resolve();

    if (preload) {
      ensureReady().catch(() => undefined);
    }
  }, [ensureReady, preload, resetJobProgress]);

  useEffect(() => {
    if (preload) {
      ensureReady().catch(() => undefined);
    }
    // Only run once on mount (and once more if `preload` flips on); `ensureReady`
    // itself de-dupes in-flight loads so this never triggers a duplicate load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preload]);

  useEffect(() => {
    return () => {
      clearEstimateTimer();
      clearStallTimer();
      prefetchCacheRef.current.forEach((entry) => entry.controller.abort());
      prefetchCacheRef.current.clear();
      workerRef.current?.terminate();
      // Reset refs so a remount (e.g. React StrictMode's mount/unmount/mount in dev)
      // creates a fresh worker instead of waiting on the terminated one forever.
      workerRef.current = null;
      readyRef.current = null;
      deviceRef.current = null;
      pendingJobsRef.current.forEach((job) => job.reject(new Error('Transcriber unmounted')));
      pendingJobsRef.current.clear();
      queueRef.current = Promise.resolve();
    };
  }, []);

  return {
    stage,
    progress,
    device,
    error,
    modelLoadProgress,
    downloadProgress,
    transcribeProgress,
    prefetchProgress,
    transcribe,
    prefetch,
    cancel,
  };
};

export type Transcriber = ReturnType<typeof useTranscriber>;

export default useTranscriber;
